import { useState } from "react";
import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageTitle from "@/components/base/PageTitle";

const faqs = [
  {
    icon: "ri-truck-line",
    question: "HOW LONG DOES SHIPPING TAKE?",
    answer:
      "Most sourced diamonds are delivered within approximately 2-3 weeks depending on sourcing availability, customization requirements, and customs processing timelines.",
  },
  {
    icon: "ri-hammer-line",
    question: "HOW LONG DOES A CUSTOM SETTING TAKE TO CREATE?",
    answer:
      "Custom ring settings require a minimum of 25 days to design and craft, not including shipping time. Each piece is meticulously handcrafted by our artisans to match your exact specifications.",
  },
  {
    icon: "ri-search-line",
    question: "HOW MANY DAYS DO I HAVE TO INSPECT FOR DAMAGES?",
    answer:
      "You must inspect your diamond and report any concerns or damages within 3 days of receiving the diamond.",
  },
  {
    icon: "ri-arrow-go-back-line",
    question: "WHAT IS YOUR RETURN POLICY?",
    answer:
      "All sales are final. We do not accept returns for change of mind, buyer's remorse, or specification changes after order confirmation. The only exceptions are verified quality defects, shipment damage, or incorrect item delivery — and these must be reported in writing within 3 days of receipt. In verified cases, we may offer a replacement diamond of equivalent value, partial refund, or store credit at our sole discretion. If a return is authorized, the client is responsible for return shipping and insurance.",
  },
  {
    icon: "ri-shield-check-line",
    question: "ARE THE DIAMONDS IGI-CERTIFIED?",
    answer:
      "Yes. All diamonds sourced by Zayan Atelier are independently certified by IGI (International Gemological Institute) for your peace of mind.",
  },
  {
    icon: "ri-bank-card-line",
    question: "WHAT PAYMENT METHODS DO YOU ACCEPT?",
    answer:
      "We accept bank transfers and other secure payment methods. Payment details and instructions will be provided upon confirmation of your selected diamond.",
  },
  {
    icon: "ri-global-line",
    question: "DO YOU SHIP INTERNATIONALLY?",
    answer:
      "Yes. We work with trusted global logistics and insured shipping providers to deliver diamonds securely to clients worldwide.",
  },
  {
    icon: "ri-lock-2-line",
    question: "IS MY INFORMATION SECURE?",
    answer:
      "Absolutely. We value your privacy and use industry-standard security practices to ensure your personal and payment information is always protected.",
  },
  {
    icon: "ri-customer-service-2-line",
    question: "CAN I SPEAK WITH SOMEONE DIRECTLY?",
    answer:
      "Yes. We offer personalized consultations by appointment to discuss your preferences and guide you through the sourcing journey.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const headerRef = useScrollReveal<HTMLDivElement>();
  const faqsRef = useScrollReveal<HTMLDivElement>();
  const ctaRef = useScrollReveal<HTMLDivElement>();

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <div className="min-h-screen bg-zayan-bg flex flex-col">
      <PageTitle title="FAQ - Zayan Atelier" />
      <Navbar />

      <section className="pt-[130px] md:pt-[170px] pb-16 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12 reveal" ref={headerRef}>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">
                  FREQUENTLY ASKED QUESTIONS
                </span>
                <div className="h-px w-12 bg-zayan-gold" />
              </div>
              <h1 className="font-serif text-4xl lg:text-6xl text-zayan-gold leading-tight mb-6">
                Answers to Common Questions
              </h1>
              <div className="h-px w-16 bg-zayan-gold mb-8" />
              <p className="text-zayan-textMuted text-sm leading-relaxed max-w-md">
                We&apos;re here to ensure your diamond sourcing experience is seamless, transparent, and worry-free.
              </p>
            </div>
            <div className="relative overflow-hidden rounded-lg border border-zayan-border h-64 lg:h-auto">
              <img
                src="https://static.readdy.ai/image/f050cadc4e04390e0c8f3be3f5e83a4a/9eb6b73cec9b76f2276ef53e0b5e732e.png"
                alt="Diamond in tweezers"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-zayan-bg/50 to-transparent" />
            </div>
          </div>

          {/* FAQ Accordion */}
          <div className="border border-zayan-border" ref={faqsRef}>
            {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={i}
                  className={`flex items-start gap-5 px-5 lg:px-8 py-6 border-b border-zayan-border last:border-b-0 transition-colors cursor-pointer hover:bg-zayan-gold/5 ${
                    isOpen ? "bg-zayan-bgCard/50" : ""
                  }`}
                  onClick={() => toggle(i)}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-full border border-zayan-gold/40 shrink-0">
                    <i className={`${faq.icon} text-zayan-gold text-xl`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-zayan-gold text-sm tracking-[0.15em] font-sans pt-1">
                        {faq.question}
                      </h3>
                      <div className="w-10 h-10 flex items-center justify-center shrink-0">
                        <i
                          className={`ri-arrow-down-s-line text-zayan-gold text-xl transition-transform duration-300 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      </div>
                    </div>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        isOpen ? "max-h-40 mt-3 opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <p className="text-zayan-textMuted text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Banner */}
          <div className="mt-12 border border-zayan-border bg-zayan-bgCard px-6 lg:px-12 py-10 flex flex-col md:flex-row items-center justify-between gap-6 reveal" ref={ctaRef}>
            <div className="flex items-center gap-6">
              <img
                src="https://static.readdy.ai/image/f050cadc4e04390e0c8f3be3f5e83a4a/754daf6fa5a838036e698ab0e430b19f.png"
                alt="Zayan Atelier"
                className="h-20 w-auto opacity-70"
              />
              <div className="h-12 w-px bg-zayan-border hidden md:block" />
              <div>
                <h3 className="font-serif text-2xl text-zayan-gold mb-1">
                  Still Have Questions?
                </h3>
                <p className="text-zayan-textMuted text-sm">
                  Our team is here to provide the guidance and clarity you need.
                </p>
              </div>
            </div>
            <Link
              to="/contact"
              className="shrink-0 border border-zayan-gold text-zayan-gold px-8 py-3 text-xs tracking-[0.15em] hover:bg-zayan-gold hover:text-zayan-bg transition-colors whitespace-nowrap"
            >
              CONTACT US
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}